import { sha256 } from 'js-sha256'

// Challenge-response nonce for CF-hosted funnel order submits (ENG-6754).
// GET /_cf/nonce returns executable JS (see UserPages::NoncesController /
// NonceIssuance) that calls window.__cfSdkHmacSha256 to compute the client
// signature, assigns window.__cfSdkNonce and dispatches `cf-sdk-nonce` —
// the same wire contract as the external-page SDK. The binding uuid is the
// cfhoy_visitor cookie, read server-side on both mint and verify, so this
// client never sends it.
//
// Resolves null once the budget below is spent; the submit then proceeds
// without nonce params. The nonce is always enforced, so that is a
// rejected order, not a soft fallback — hence the retry.

export type CheckoutNonce = {
  challenge: string
  ts: number
  server_sig: string
  client_sig: string
}

const NONCE_PATH = '/_cf/nonce'
// Ceiling across ALL attempts, so a slow network cannot stall checkout for
// attempts × timeout. Split evenly below — 6s each. Well under the 1-minute
// nonce TTL even if the last attempt is the one that lands.
const NONCE_TOTAL_BUDGET_MS = 12000
const NONCE_MAX_ATTEMPTS = 2

function requestNonceOnce(timeoutMs: number): Promise<CheckoutNonce | null> {
  const win = window as any

  return new Promise((resolve) => {
    win.__cfSdkNonce = null

    const script = document.createElement('script')
    let settled = false
    const finish = (value: CheckoutNonce | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      document.removeEventListener('cf-sdk-nonce', onNonce)
      script.remove()
      resolve(value)
    }
    const onNonce = () => finish(win.__cfSdkNonce || null)
    const timer = setTimeout(() => finish(null), timeoutMs)

    document.addEventListener('cf-sdk-nonce', onNonce)
    script.src = NONCE_PATH
    script.async = true
    script.onerror = () => finish(null)
    document.head.appendChild(script)
  })
}

export async function fetchCheckoutNonce(): Promise<CheckoutNonce | null> {
  const win = window as any
  // Pure-JS HMAC, not Web Crypto: matches the SDK helper the server JS calls.
  if (!win.__cfSdkHmacSha256) {
    win.__cfSdkHmacSha256 = (key: string, data: string) => sha256.hmac(key, data)
  }

  // Monotonic: a device clock correction mid-fetch would otherwise skip the retry entirely or
  // push completion past the ceiling this budget exists to guarantee.
  const elapsed = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())
  const startedAt = elapsed()

  for (let attempt = 0; attempt < NONCE_MAX_ATTEMPTS; attempt++) {
    const remaining = NONCE_TOTAL_BUDGET_MS - (elapsed() - startedAt)
    if (remaining <= 0) break

    // Even slices, not a fixed timeout plus whatever survives: a late script
    // from a previous attempt dispatches into this attempt's listener, so only
    // the last attempt's own window covers a response that never arrives.
    const nonce = await requestNonceOnce(remaining / (NONCE_MAX_ATTEMPTS - attempt))
    if (nonce) return nonce
  }

  return null
}

// Wire params verified by OrderProcessingConcern#valid_checkout_nonce?.
export function checkoutNonceParams(nonce: CheckoutNonce | null): Record<string, string> {
  if (!nonce) return {}
  return {
    sdk_challenge: nonce.challenge,
    sdk_ts: String(nonce.ts),
    sdk_server_sig: nonce.server_sig,
    sdk_client_sig: nonce.client_sig,
  }
}
